import { useCallback } from 'react';
import { useSetAtom } from 'jotai';
import { userFeaturesAtom } from 'atoms/permissions';
import { tierAtom } from 'atoms/tier';
import { tenantNameAtom } from 'atoms/tenantName';
import { branchInformationAtom } from 'atoms/branchInformation';
import apiService from 'api/apiService';
import { ClientConfiguration } from 'interfaces/clientConfiguration';

/** Stores the signed-in user's tenant configuration: enabled features, tier, branch limits and name. */
export function useApplyClientConfiguration() {
  const setFeatures = useSetAtom(userFeaturesAtom);
  const setTier = useSetAtom(tierAtom);
  const setTenantName = useSetAtom(tenantNameAtom);
  const setBranchInformation = useSetAtom(branchInformationAtom);

  return useCallback(
    (config: ClientConfiguration) => {
      setTier(config.tierInformation);
      setBranchInformation(config.branchInformation);
      setFeatures(config.enabledAddOns ?? []);
      setTenantName(config.tenantName);
    },
    [setFeatures, setTier, setTenantName, setBranchInformation],
  );
}

/**
 * Reloads the signed-in user's tenant configuration, so a change to the tenant (features, tier,
 * limits) takes effect without reloading the page. Branches and the selected branch are left as is.
 */
export function useRefreshUserConfig() {
  const applyClientConfiguration = useApplyClientConfiguration();

  return useCallback(async () => {
    applyClientConfiguration(await apiService.clientConfiguration.get());
  }, [applyClientConfiguration]);
}
